import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import { fetchPINoticeDetails } from '../../services/publicTendersApi';
import Breadcrumbs from '../../components/Breadcrumbs';
import Utility from './../../utils/Utility';
import ItemLoadingHolder from '../../components/LoadingHolders/ItemLoadingHolder';
import ProjectBadge from '../../components/ProjectBadge/ProjectBadge';
import SaveProcedureButton from '../../components/SaveProcedureButton/SaveProcedureButton';
import ProcedurePaywall from '../../components/ProcedurePaywall/ProcedurePaywall';
import { slugify } from '../../utils/slugify';
import TruncatedText from '../../components/TruncatedText/TruncatedText';

const PriorInformationDetails = () => {
  const { noticeNo: id } = useParams(); // URL param now holds mongoId
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchPINoticeDetails(id)
      .then((result) => setData(result))
      .catch((err) => {
        console.error('Error fetching PI notice:', err);
        setError('Anuntul de intentie nu a fost gasit.');
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <ItemLoadingHolder />;

  if (error || !data) {
    return (
      <section className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-green-100 py-16">
        <div className="container max-w-5xl mx-auto text-center">
          <h1 className="text-3xl font-bold text-red-600 mb-4">Anunt negasit</h1>
          <p className="text-gray-600 mb-6">{error || 'Anuntul de intentie nu a fost gasit in baza de date.'}</p>
          <Link to="/anunturi-intentie" className="text-indigo-600 hover:underline font-semibold">&larr; Inapoi la Anunturi de Intentie</Link>
        </div>
      </section>
    );
  }

  const view = data.piNoticeView || {};
  const ext = data.extension || {};
  const general = view.sectionGeneral || {};
  const v2 = view.piNoticeV2 || {};
  const section1 = v2.section1 || {};
  const section2List = v2.section2 || [];
  const section3 = v2.section3 || {};
  const section4 = v2.section4 || {};
  const section6 = v2.section6 || {};
  const caAddress = section1.section1_1?.caAddress || {};

  const noticeNo = view.noticeNo || ext.noticeNo || id;
  const title = general.contractTitle || ext.auctionTitle || noticeNo;
  const authorityName = caAddress.officialName || section1.section1_1?.entityName || ext.authorityName || 'N/A';
  const cui = caAddress.nationalIDNumber || section1.section1_1?.entityCUI || ext.authorityCUI || '';
  const contractType = general.sysAcquisitionContractType?.text || ext.contractType || 'N/A';
  const estimatedValue = ext.estimatedValue || 0;
  const currency = ext.currency || 'RON';
  const cpvCode = ext.cpVCode || ext.cpvCode || '';
  const cpvName = ext.cpvName || '';
  const publishDate = view.publishDate ? Utility.FormatDate(view.publishDate) : 'N/A';
  const awardStartDate = section4.section4_2?.awardProceduresStartDate
    ? Utility.FormatDate(section4.section4_2.awardProceduresStartDate) : null;
  const legislation = general.sysLegislationType?.text || null;
  const noticeState = view.sysNoticeState?.text || null;
  const authorityType = section1.section1_4?.contractingAuthorityType?.text || null;
  const mainActivity = section1.section1_5?.mainActivity?.text || null;
  const languages = section4.section4_2?.ueLanguage?.map(l => l.text).join(', ') || null;
  const procedureType = section4.section4_1?.sysProcedureType?.text || null;
  const deposits = section3.section3_1?.deposits || null;
  const financialCapacity = section3.section3_1?.financialCapacity || null;
  const technicalCapacity = section3.section3_1?.technicalCapacity || null;
  const contractPerformanceConditions = section3.section3_2?.contractPerformanceConditions || null;
  const additionalInfo = section6.section6_3?.additionalInfo || null;

  const breadcrumbLinks = [
    { href: '/', label: 'Home' },
    { href: '/anunturi-intentie', label: 'Anunturi de Intentie' },
    { label: title },
  ];

  const defaultTitle = 'Licitatii-Constructii – Anunțuri de intenție';
  const defaultDesc = 'Informații despre anunțurile de intenție publicate în SEAP.';
  const seoTitle = title && title !== noticeNo
    ? `${title} – Licitatii-Constructii`
    : defaultTitle;
  const seoDesc = title && title !== noticeNo
    ? `Informații despre anunțul de intenție ${title}${authorityName && authorityName !== 'N/A' ? `, publicat de ${authorityName}` : ''}.`
    : defaultDesc;
  const canonicalUrl = `${import.meta.env.VITE_DOMAIN}/anunturi-intentie/${slugify(title)}/${id}`;
  const ogImage = `${import.meta.env.VITE_DOMAIN}/og-default.png`;

  return (
    <>
      <SeoPageComponent
        pageTitle={seoTitle}
        pageDescription={seoDesc}
        pageUrl={canonicalUrl}
        logoUrl={ogImage}
        keywords={`anunturi intentie, ${cpvCode || ''}, ${title}`}
        canonicalUrl={canonicalUrl}
        robotsContent="index,follow"
      />
      <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />

      {/* Titlu */}
      <section className="pt-10 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">
          <h1 className="text-2xl md:text-4xl font-extrabold text-indigo-700 text-center mb-4 break-words drop-shadow-xl">
            {title}
          </h1>
        </div>
      </section>

      {/* Summary bar */}
      <section className="pb-6 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-2 md:gap-4 bg-white/80 rounded-xl shadow-lg px-4 py-4 md:py-6 mb-6 border border-indigo-100">
            <div className="flex flex-wrap gap-2 justify-center items-center w-full md:w-auto">
              {!data.isPartial && (
                <span className="bg-amber-100 text-amber-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-amber-200 shadow-sm">
                  {noticeNo}
                </span>
              )}
              <span className="bg-yellow-100 text-yellow-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-yellow-200 shadow-sm">
                {authorityName}
              </span>
              <span className="bg-purple-100 text-purple-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-purple-200 shadow-sm">
                {contractType}
              </span>
              <span className="bg-indigo-100 text-indigo-700 text-xs md:text-sm font-bold px-3 py-1 rounded-full border border-indigo-200 shadow-sm">
                {Utility.ConvertEstimatedValue(estimatedValue)} {currency}
              </span>
              {cpvCode && (
                <span className="bg-blue-100 text-blue-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-blue-200 shadow-sm">
                  CPV: {cpvCode}
                </span>
              )}
              {noticeState && (
                <span className="bg-green-100 text-green-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-green-200 shadow-sm">
                  {noticeState}
                </span>
              )}
            </div>
            <div className="w-full md:w-auto flex justify-center md:justify-end mt-2 md:mt-0">
              <SaveProcedureButton noticeNo={noticeNo} />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-16 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">

          {/* Row 1: Detalii (4col) | Termene (3col) | Contact (2col) | Proiect (3col) */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
            {/* Detalii Achizitie — 4/12 */}
            <div className="md:col-span-4 bg-white shadow-lg rounded-xl p-6 flex flex-col gap-4 hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-indigo-600 text-lg font-bold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                Detalii Achizitie
              </h2>
              <div className="flex flex-col gap-2 text-base text-gray-700">
                <div><span className="font-medium">Numar:</span> <span className="text-amber-700 font-semibold bg-amber-50 px-2 py-0.5 rounded text-sm">{noticeNo}</span></div>
                <div><span className="font-medium">Tip contract:</span> <span className="text-purple-700 font-semibold bg-purple-50 px-2 py-0.5 rounded text-sm">{contractType}</span></div>
                <div><span className="font-medium">Valoare estimata:</span> <span className="text-green-700 font-bold bg-green-50 px-2 py-0.5 rounded text-sm">{Utility.ConvertEstimatedValue(estimatedValue)} {currency}</span></div>
                {cpvCode && <div><span className="font-medium">Cod CPV:</span> <span className="text-blue-700 font-semibold bg-blue-50 px-2 py-0.5 rounded text-sm">{cpvCode}{cpvName ? ` - ${cpvName}` : ''}</span></div>}
                {legislation && <div><span className="font-medium">Legislatie:</span> <span className="text-sm">{legislation}</span></div>}
                {languages && <div><span className="font-medium">Limba:</span> <span className="text-sm">{languages}</span></div>}
              </div>
              <a href={`https://e-licitatie.ro/pub/notices/pi-notice/view/v2/${view.piNoticeId}`}
                target="_blank" rel="noopener noreferrer"
                className="mt-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:from-indigo-600 hover:to-indigo-700 transition-all text-sm">
                Vezi pe SEAP &rarr;
              </a>
            </div>

            {/* Termene — 3/12 */}
            <div className="md:col-span-3 bg-white shadow-lg rounded-xl p-6 flex flex-col gap-4 hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-indigo-600 text-lg font-bold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Termene
              </h2>
              <div className="flex flex-col gap-2 text-sm text-gray-700">
                <div><span className="font-medium">Publicare:</span> {publishDate}</div>
                {awardStartDate && <div><span className="font-medium">Incepere:</span> {awardStartDate}</div>}
                {section2List.length > 0 && section2List[0].section2_3?.estimatedPublicationDate && (
                  <div><span className="font-medium">Procedura:</span> {Utility.FormatDate(section2List[0].section2_3.estimatedPublicationDate)}</div>
                )}
              </div>
              <div className="flex flex-wrap gap-1 mt-auto">
                {procedureType && <span className="bg-indigo-100 text-indigo-700 text-xs font-semibold px-2 py-1 rounded-full">{procedureType}</span>}
                {section4.section4_1?.masterAgreement && <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full">Acord-cadru</span>}
                {section4.section4_1?.electronicAuction && <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full">Licitatie electronica</span>}
                {section4.section4_1?.isPPP && <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-1 rounded-full">PPP</span>}
                {section4.section4_1?.procurementInvolvesSad && <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full">SAD</span>}
              </div>
            </div>

            {/* Contact Autoritate — 2/12 */}
            <div className="md:col-span-2 bg-white shadow-lg rounded-xl p-6 flex flex-col gap-3 hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-indigo-600 text-lg font-bold mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Contact
              </h2>
              <div className="flex flex-col gap-1.5 text-sm text-gray-700">
                <div><span className="font-medium">Autoritate:</span> <span className="text-yellow-700 font-semibold bg-yellow-50 px-2 py-0.5 rounded text-xs">{authorityName}</span></div>
                {cui && <div><span className="font-medium">CUI:</span> <span className="text-xs">{cui}</span></div>}
                {authorityType && <div><span className="font-medium">Tip:</span> <span className="text-xs">{authorityType}</span></div>}
                {caAddress.email && <div><span className="font-medium">Email:</span> <span className="text-xs">{caAddress.email}</span></div>}
                {caAddress.phone && <div><span className="font-medium">Tel:</span> <span className="text-xs">{caAddress.phone}</span></div>}
                {caAddress.contactPerson && <div><span className="font-medium">Contact:</span> <span className="text-xs">{caAddress.contactPerson}</span></div>}
                {caAddress.city && <div><span className="font-medium">Loc:</span> <span className="text-xs">{caAddress.city}{caAddress.county?.text ? `, ${caAddress.county.text}` : ''}</span></div>}
                {caAddress.nutsCodeItem?.text && <div><span className="font-medium">NUTS:</span> <span className="text-xs">{caAddress.nutsCodeItem.text}</span></div>}
              </div>
            </div>

            {/* Proiect — 3/12 */}
            <div className="md:col-span-3">
              <ProjectBadge auctionId={noticeNo} variant="section" />
            </div>
          </div>

          {/* Row 3: Loturi / Obiectul contractului */}
          {section2List.length > 0 && section2List.map((s2, idx) => {
            const lots = s2.section2_2?.lots?.items || [];
            return (
              <div key={idx} className="bg-white shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow duration-300 mb-6">
                <h2 className="text-indigo-600 text-lg font-bold mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                  Obiectul contractului {section2List.length > 1 ? `#${idx + 1}` : ''}
                </h2>
                {s2.section2_1 && (
                  <div className="flex flex-col gap-2 text-base text-gray-700 mb-4">
                    {s2.section2_1.title && <div><span className="font-medium">Titlu:</span> {s2.section2_1.title}</div>}
                    {s2.section2_1.description && <div><span className="font-medium">Descriere:</span> {s2.section2_1.description}</div>}
                    {s2.section2_1.referenceNo && <div><span className="font-medium">Referinta PAAP:</span> <span className="text-indigo-700 font-semibold bg-indigo-50 px-2 py-0.5 rounded text-sm">{s2.section2_1.referenceNo}</span></div>}
                    {s2.section2_1.mainCPVCode && <div><span className="font-medium">CPV principal:</span> <span className="text-blue-700 bg-blue-50 px-2 py-0.5 rounded text-sm">{s2.section2_1.mainCPVCode.code} - {s2.section2_1.mainCPVCode.name}</span></div>}
                    {s2.section2_1.contractInfo?.estimatedValue && <div><span className="font-medium">Valoare:</span> <span className="text-green-700 font-bold bg-green-50 px-2 py-0.5 rounded text-sm">{s2.section2_1.contractInfo.estimatedValue.toLocaleString('ro-RO')} {s2.section2_1.contractInfo.currency?.text || 'RON'}</span></div>}
                    {s2.section2_1.hasLots !== undefined && (
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full w-fit ${s2.section2_1.hasLots ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                        {s2.section2_1.hasLots ? 'Impartit in loturi' : 'Fara loturi'}
                      </span>
                    )}
                  </div>
                )}

                {lots.length > 0 && lots.map((lot, lIdx) => (
                  <div key={lIdx} className="border-l-4 border-amber-400 pl-4 mb-4 bg-amber-50/50 rounded-r-lg p-4">
                    <h3 className="font-bold text-amber-800 mb-2">Lot {lot.lotNo || lIdx + 1}: {lot.title || '-'}</h3>
                    <div className="flex flex-col gap-1 text-sm text-gray-700">
                      {(lot.descriptionOfProcurement || lot.shortDescription) && <div><span className="font-medium">Descriere:</span> {lot.descriptionOfProcurement || lot.shortDescription}</div>}
                      {lot.mainSiteOrPlaceOfPerformance && <div><span className="font-medium">Locul executiei:</span> {lot.mainSiteOrPlaceOfPerformance}</div>}
                      {lot.nutsCodes?.text && <div><span className="font-medium">NUTS:</span> {lot.nutsCodes.text}</div>}
                      {lot.mainCPVCode && <div><span className="font-medium">CPV:</span> <span className="text-blue-700 bg-blue-50 px-2 py-0.5 rounded text-xs">{lot.mainCPVCode.code} - {lot.mainCPVCode.name}</span></div>}
                      {lot.estimatedValue?.estimatedValue && <div><span className="font-medium">Valoare:</span> <span className="text-green-700 font-bold bg-green-50 px-2 py-0.5 rounded text-xs">{lot.estimatedValue.estimatedValue.toLocaleString('ro-RO')} {lot.estimatedValue.currency?.text || 'RON'}</span></div>}
                      {lot.contractDuration?.monthDuration && <div><span className="font-medium">Durata:</span> {lot.contractDuration.monthDuration} luni</div>}
                      {lot.contractDuration?.dayDuration && <div><span className="font-medium">Durata:</span> {lot.contractDuration.dayDuration} zile</div>}
                      {lot.contractDuration?.startDate && <div><span className="font-medium">Inceput:</span> {Utility.FormatDate(lot.contractDuration.startDate)}</div>}
                      {lot.contractDuration?.completionDate && <div><span className="font-medium">Finalizare:</span> {Utility.FormatDate(lot.contractDuration.completionDate)}</div>}
                      {lot.cpvCodes?.length > 0 && (
                        <div className="mt-1">
                          <span className="font-medium">CPV suplimentare:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {lot.cpvCodes.map((c, ci) => (
                              <span key={ci} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{c.code} - {c.name}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {lot.additionalInformation && <p className="text-gray-600 mt-2 italic text-xs">{lot.additionalInformation}</p>}
                    </div>
                  </div>
                ))}

                {s2.section2_3?.estimatedPublicationDate && (
                  <div className="mt-3 bg-indigo-50 rounded-lg px-4 py-2 text-sm">
                    <span className="font-medium text-indigo-700">Data estimata publicare procedura:</span>{' '}
                    <span className="text-indigo-900 font-bold">{Utility.FormatDate(s2.section2_3.estimatedPublicationDate)}</span>
                  </div>
                )}
              </div>
            );
          })}

          {/* Row 4: Cerinte calificare + Informatii suplimentare */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {deposits && (
              <div className="bg-white shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow duration-300">
                <h2 className="text-indigo-600 text-lg font-bold mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Garantii / Depozite
                </h2>
                <TruncatedText text={deposits} />
              </div>
            )}
            {financialCapacity && (
              <div className="bg-white shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow duration-300">
                <h2 className="text-indigo-600 text-lg font-bold mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Capacitate financiara
                </h2>
                <TruncatedText text={financialCapacity} />
              </div>
            )}
            {technicalCapacity && (
              <div className="bg-white shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow duration-300">
                <h2 className="text-indigo-600 text-lg font-bold mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  Capacitate tehnica
                </h2>
                <TruncatedText text={technicalCapacity} />
              </div>
            )}
            {contractPerformanceConditions && (
              <div className="bg-white shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow duration-300">
                <h2 className="text-indigo-600 text-lg font-bold mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                  Conditii executare
                </h2>
                <TruncatedText text={contractPerformanceConditions} />
              </div>
            )}
            {additionalInfo && (
              <div className="bg-white shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow duration-300">
                <h2 className="text-indigo-600 text-lg font-bold mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                  Informatii suplimentare
                </h2>
                <TruncatedText text={additionalInfo} />
              </div>
            )}
          </div>

        </div>
      </section>

      {/* Paywall pentru utilizatorii fără abonament */}
      {data.isPartial && <ProcedurePaywall />}
    </>
  );
};

export default PriorInformationDetails;
